import { Quaternion, Vector3 } from 'three';
import { DEFAULT_ONE_EURO, LowPassFilter, smoothingAlpha, type OneEuroParams } from './OneEuroFilter';

/**
 * One Euro filter for rotations.
 *
 * The scalar One Euro structure is kept, but:
 *  - the "low-pass" step is a spherical interpolation (slerp) between the
 *    previous filtered rotation and the new raw rotation;
 *  - the "speed" driving the adaptive cutoff is the angular speed (rad/s) of
 *    the raw signal, estimated from the geodesic angle between consecutive
 *    raw quaternions and low-passed at dCutoff;
 *  - the quaternion double cover (q and -q are the same rotation) is handled
 *    by flipping the incoming quaternion into the hemisphere of the previous
 *    one, so a sign change in the tracker never produces a 360° spin.
 */
export class OneEuroQuaternion {
  private params: OneEuroParams;
  private readonly hatQ = new Quaternion();
  private readonly lastRaw = new Quaternion();
  private readonly speedFilter = new LowPassFilter();
  private hasValue = false;
  private lastTime: number | null = null;
  private lastSpeed = 0;
  private lastRawSpeed = 0;
  private lastCutoff = 0;
  private readonly tmpQ = new Quaternion();
  private readonly axisTmp = new Vector3();
  /** Angular velocity of the *filtered* output, as axis * rad/s. */
  private readonly omega = new Vector3();
  private readonly prevHatQ = new Quaternion();

  constructor(params: Partial<OneEuroParams> = {}) {
    this.params = { ...DEFAULT_ONE_EURO, ...params };
  }

  setParams(params: Partial<OneEuroParams>): void {
    this.params = { ...this.params, ...params };
  }

  getParams(): OneEuroParams {
    return { ...this.params };
  }

  filter(rawIn: Quaternion, tSeconds: number, out: Quaternion = new Quaternion()): Quaternion {
    let dt = this.lastTime === null ? 1 / 60 : tSeconds - this.lastTime;
    if (!(dt > 0)) dt = 1 / 60;
    this.lastTime = tSeconds;

    const raw = this.tmpQ.copy(rawIn).normalize();

    if (!this.hasValue) {
      this.hatQ.copy(raw);
      this.prevHatQ.copy(raw);
      this.lastRaw.copy(raw);
      this.hasValue = true;
      this.omega.set(0, 0, 0);
      return out.copy(raw);
    }

    // Keep the incoming quaternion on the same hemisphere as the last one.
    if (raw.dot(this.lastRaw) < 0) {
      raw.set(-raw.x, -raw.y, -raw.z, -raw.w);
    }

    // Angular speed of the raw signal: theta = 2*acos(|<q1,q2>|).
    const d = Math.min(1, Math.abs(raw.dot(this.lastRaw)));
    const angle = 2 * Math.acos(d);
    this.lastRaw.copy(raw);
    this.lastRawSpeed = angle / dt;
    const speed = this.speedFilter.filter(this.lastRawSpeed, smoothingAlpha(this.params.dCutoff, dt));
    this.lastSpeed = speed;

    const cutoff = this.params.minCutoff + this.params.beta * speed;
    this.lastCutoff = cutoff;
    const a = smoothingAlpha(cutoff, dt);

    this.prevHatQ.copy(this.hatQ);
    if (this.hatQ.dot(raw) < 0) {
      raw.set(-raw.x, -raw.y, -raw.z, -raw.w);
    }
    this.hatQ.slerp(raw, a);

    // Angular velocity of the filtered output (for prediction).
    const delta = this.tmpQ.copy(this.prevHatQ).invert().premultiply(this.hatQ).normalize();
    if (delta.w < 0) delta.set(-delta.x, -delta.y, -delta.z, -delta.w);
    const half = Math.acos(Math.min(1, delta.w));
    const s = Math.sin(half);
    if (s > 1e-6) {
      this.axisTmp.set(delta.x / s, delta.y / s, delta.z / s);
      this.omega.copy(this.axisTmp).multiplyScalar((2 * half) / dt);
    } else {
      this.omega.set(0, 0, 0);
    }

    return out.copy(this.hatQ);
  }

  /** Low-passed angular speed of the raw input (rad/s). */
  get speed(): number {
    return this.lastSpeed;
  }

  /** Instantaneous angular speed between the last two raw samples (rad/s), unfiltered. */
  get rawSpeed(): number {
    return this.lastRawSpeed;
  }

  /** Cutoff used by the last sample (Hz): the filter's lag at constant angular velocity is 1 / (2π · cutoff). */
  get currentCutoff(): number {
    return this.lastCutoff;
  }

  /** Angular velocity of the filtered output as axis * rad/s (world frame). */
  get angularVelocity(): Vector3 {
    return this.omega;
  }

  get value(): Quaternion | null {
    return this.hasValue ? this.hatQ : null;
  }

  reset(): void {
    this.hasValue = false;
    this.lastTime = null;
    this.lastSpeed = 0;
    this.lastRawSpeed = 0;
    this.lastCutoff = 0;
    this.speedFilter.reset();
    this.omega.set(0, 0, 0);
  }
}
